import React from 'react';
import type { StatusTone } from '../../support/campaigns';
import s from './campaignUser.module.css';
import CampaignMarkdown from './CampaignMarkdown';

/**
 * Shared primitives for the user-facing Campaigns runner, ported from the `campaign-test` reference
 * app's `ui/primitives.tsx`. Everything renders inside a `<CampaignRoot>` wrapper, which carries the
 * scoped `--*` design tokens (see `campaignUser.module.css`), so these components — and the runner
 * pages that compose them — get the reference look without leaking styles into the rest of the portal.
 *
 * Icons come from the existing MUI set (via `StepKindIcon`); MUI controls are fine inside the scoped
 * surface, we simply do not restyle the global MUI theme.
 */

export const cx = (...parts: (string | false | null | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

/** The raw CSS-module class map, for pages that need a class the primitives do not wrap. */
export const ui = s;

/**
 * Wraps a subtree in the scoped design system. Put this at the root of every Campaigns page.
 * `theme` sets `data-campaign-theme`, which retints every descendant (chrome + the Frontier runner)
 * by overriding the scoped `--*` tokens — see the `.campaignRoot[data-campaign-theme=…]` blocks in
 * campaignUser.module.css. Absent → the default Cyber Academy tokens.
 */
export const CampaignRoot: React.FC<{
  className?: string;
  theme?: string | null;
  children: React.ReactNode;
}> = ({ className, theme, children }) => (
  <div className={cx(s.campaignRoot, className)} data-campaign-theme={theme || undefined}>
    {children}
  </div>
);

export const STATUS_TEXT: Record<StatusTone, string> = {
  done: 'Done',
  progress: 'In progress',
  notstarted: 'Not started',
};

const STATUS_PILL_CLASS: Record<StatusTone, string> = {
  done: s.spDone,
  progress: s.spProgress,
  notstarted: s.spNotstarted,
};

export const StatusPill: React.FC<{
  tone: StatusTone;
  label?: string;
  className?: string;
}> = ({ tone, label, className }) => (
  <span className={cx(s.statusPill, STATUS_PILL_CLASS[tone], className)}>
    {label ?? STATUS_TEXT[tone]}
  </span>
);

/** The accent "eyebrow" pill used above page and step titles. */
export const PhaseTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className={s.phaseTag}>{children}</span>
);

/** A dim, neutral metadata chip (duration, track, etc.). Optional `title` for a hover tooltip. */
export const MetaTag: React.FC<{ children: React.ReactNode; title?: string }> = ({
  children,
  title,
}) => (
  <span className={s.metaTag} title={title}>
    {children}
  </span>
);

export const TagRow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cx(s.tagRow, className)}>{children}</div>;

interface BlockProps {
  icon?: React.ReactNode;
  title: string;
  lead?: string;
  headerRight?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** The universal step-section wrapper: icon chip + title (+ optional right slot) over content. */
export const Block: React.FC<BlockProps> = ({
  icon,
  title,
  lead,
  headerRight,
  className,
  children,
}) => (
  <section className={cx(s.block, className)}>
    <div className={s.blockHead}>
      {icon && <div className={s.iconChip}>{icon}</div>}
      <h4>{title}</h4>
      {headerRight}
    </div>
    {lead && <CampaignMarkdown className={s.blockLead}>{lead}</CampaignMarkdown>}
    {children}
  </section>
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  primary?: boolean;
  small?: boolean;
};

/** A button styled in the scoped system. `primary` fills with the accent colour. */
export const Btn: React.FC<ButtonProps> = ({ primary, small, className, children, ...rest }) => (
  <button
    type="button"
    className={cx(s.btn, primary && s.primary, small && s.btnSm, className)}
    {...rest}
  >
    {children}
  </button>
);

type LinkButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  primary?: boolean;
  small?: boolean;
};

/** An anchor that looks like `Btn` — for external launches (`target="_blank"`). */
export const LinkBtn: React.FC<LinkButtonProps> = ({
  primary,
  small,
  className,
  children,
  ...rest
}) => (
  <a className={cx(s.btn, primary && s.primary, small && s.btnSm, className)} {...rest}>
    {children}
  </a>
);

export const ProgressBar: React.FC<{ percent: number; label?: string }> = ({ percent, label }) => {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div className={s.progressBar}>
      <div
        className={s.progressTrack}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={s.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={s.progressLabel}>{label ?? `${pct}%`}</span>
    </div>
  );
};

interface CourseCardProps {
  title: string;
  description?: string | null;
  href: string;
  done?: boolean;
  /** Fired when the external course link is opened (e.g. to record a launch). */
  onLaunch?: () => void;
  ctaLabel?: string;
}

/**
 * The external-content reference card (Cyber Academy course, Moodle workshop, …). Mirrors the
 * reference app's training-gate card: the courseware opens in a new tab — never an iframe — and
 * completion is tracked elsewhere (the referenced journey map / delivery status).
 */
export const CourseCard: React.FC<CourseCardProps> = ({
  title,
  description,
  href,
  done,
  onLaunch,
  ctaLabel,
}) => (
  <div className={cx(s.courseCard, done && s.courseCardDone)}>
    <div className={s.courseHead}>
      <h3>{title}</h3>
      {done && <StatusPill tone="done" />}
    </div>
    {description && <p>{description}</p>}
    <div className={s.courseActions}>
      <LinkBtn
        primary={!done}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLaunch}
      >
        {ctaLabel ?? (done ? 'Reopen course ↗' : 'Start course ↗')}
      </LinkBtn>
      {done && <span className={s.courseNote}>✓ Completed</span>}
    </div>
  </div>
);

export const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={s.empty}>{children}</div>
);
